import { cn } from "@tcg/ui";
import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import { formatYen } from "@/lib/money";

export interface MoneyTextProps extends Omit<ComponentProps<"span">, "children"> {
  /** Integer JPY exactly as the server sent it (税込) — never computed on the tablet. */
  amount: number;
  /** Show a leading minus (discounts, refunds): the value itself stays positive. */
  negative?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "hero";
}

const SIZES = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-xl",
  xl: "text-3xl",
  hero: "text-6xl",
} as const;

/** `¥19,000` — yen without decimals, tabular digits (design/05 §3, T07-22 MoneyText). */
export function MoneyText({
  amount,
  negative = false,
  size = "md",
  className,
  ...props
}: MoneyTextProps) {
  const { i18n } = useTranslation();
  const text = formatYen(amount, i18n.resolvedLanguage ?? "ja");
  return (
    <span
      data-slot="money"
      className={cn("tabular-nums font-semibold", SIZES[size], className)}
      {...props}
    >
      {negative && amount > 0 ? `−${text}` : text}
    </span>
  );
}

/** `(amount) => "¥19,000"` in the staff member's language — for amounts inside a sentence. */
export function useYen(): (amount: number) => string {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "ja";
  return (amount: number) => formatYen(amount, locale);
}
